import React from 'react';

import { useTranslation } from 'react-i18next';
import { Formik, FormikState, FormikValues } from 'formik';
import { Button, Divider, Segment } from 'semantic';

import { FormikErrorsMessage } from './FormikErrorsMessage';

export type FormComponent<Values> = React.ComponentType<{
  values: FormikState<Values>['values'];
  setFieldValue: (
    field: string,
    value: number | string | boolean | Date
  ) => void;
  basePath?: string;
  errors?: {};
  touched?: {};
}>;

export type PreviewComponent<Values> = React.ComponentType<{
  values: FormikState<Values>['values'];
  index?: number;
  onEdit?: () => void;
  onRemove?: () => Promise<void> | undefined;
  dragHandleProps?: {};
  currency: string;
}>;

type Props<Values> = {
  values: Values;
  basePath?: string;
  openInEditMode?: boolean;
  removeOnCancel?: boolean;
  currency: string;
  validationSchema?: {};
  index?: number;
  onSubmit: (values: Values) => void;
  onRemove?: () => Promise<void> | undefined;
  onParentValidate?: () => void;
  dragHandleProps?: React.HTMLAttributes<HTMLDivElement>;
  formComponent: FormComponent<Values>;
  previewComponent: PreviewComponent<Values>;
  hideErrors?: boolean;
  onEdit?: () => void;
  onCancel?: () => void;
};

// FormController is a helper component that handles editing vs not editing state of groups and policies (sub) forms
// It uses Formik to handle the form state and validation and requires a Form and Preview component as input
// The reason we did this decoupling, is to prevent duplicate code (at the cost of some complexity)
// But the more important reason is that some of these Form components may be used both as their own form (with save and cancel buttons),
// Or as a subform living directly within a larger form (with no save and cancel buttons of its own)
export const FormController = <Values extends FormikValues>(
  props: Props<Values>
) => {
  const [editMode, setEditMode] = React.useState(!!props.openInEditMode);

  const { t } = useTranslation();

  const submit = (values: Values) => {
    props.onSubmit && props.onSubmit(values);
    setEditMode(false);
  };

  const cancel = () => {
    props.removeOnCancel && props.onRemove && props.onRemove();
    props.onCancel?.();
    setEditMode(false);
  };

  return (
    <Formik
      initialValues={props.values || {}}
      validationSchema={props.validationSchema}
      onSubmit={submit}
      onReset={cancel}
      validateOnBlur={true}
      validateOnChange={true}
      validateOnMount={true}>
      {({
        handleSubmit,
        handleReset,
        values,
        setFieldValue,
        isValid,
        errors,
        submitCount,
      }) => (
        <>
          {editMode && (
            <Segment>
              <props.formComponent
                values={values}
                setFieldValue={setFieldValue}
                basePath={props.basePath}
              />
              <div />
              {!props.hideErrors && submitCount > 0 && (
                <FormikErrorsMessage errors={errors} />
              )}
              <Divider hidden />
              <Button
                content={t('tariff.save', 'Save')}
                as="button"
                style={{ marginLeft: '0' }}
                onClick={handleSubmit}
                disabled={!isValid}
              />
              <Button
                content={t('tariff.cancel', 'Cancel')}
                as="button"
                basic
                onClick={handleReset}
              />
            </Segment>
          )}
          {!editMode && (
            <props.previewComponent
              values={values}
              index={props.index}
              onEdit={() => {
                setEditMode(true);
                props.onEdit?.();
              }}
              onRemove={props.onRemove}
              dragHandleProps={props.dragHandleProps}
              currency={props.currency}
            />
          )}
        </>
      )}
    </Formik>
  );
};
